import { useState, useEffect } from 'react';
import ax from '@/lib/apiClient';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Building2, ScrollText, Search, Pencil, GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import { TransferRequests } from '@/components/TransferRequests';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Master admin only. Lives under Settings so it stays out of the way.
export default function AdminSections() {
  const [tab, setTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [auditLog, setAuditLog] = useState([]);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [editAcct, setEditAcct] = useState(null);
  const [keyInput, setKeyInput] = useState('');
  const [keyError, setKeyError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAccounts(); fetchAuditLog(); }, []);

  const fetchAccounts = async (q = '') => {
    try { const { data } = await ax.get(`${API}/admin/accounts?q=${encodeURIComponent(q)}`); setAccounts(data.accounts); setTotalAccounts(data.total); } catch {}
  };
  const fetchAuditLog = async () => { try { const { data } = await ax.get(`${API}/admin/audit-log?limit=20`); setAuditLog(data.entries); } catch {} };
  const handleSearch = (e) => { e.preventDefault(); fetchAccounts(searchQuery); };

  const openKeyEditor = (a) => { setEditAcct(a); setKeyInput(''); setKeyError(''); };
  const saveLicenseKey = async () => {
    if (!editAcct) return;
    setSaving(true); setKeyError('');
    try {
      await ax.put(`${API}/account/license-key`, { account_id: editAcct.account_id, license_key: keyInput });
      toast.success('License key updated');
      setEditAcct(null);
      fetchAccounts(searchQuery);
      fetchAuditLog();
    } catch (err) {
      setKeyError(err.response?.data?.detail || 'Could not save the license key.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-admin-sections" className="">
            <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-[#141b50]/80 border border-[rgba(251,221,104,0.15)] p-1 rounded-xl">
          <TabsTrigger data-testid="settings-tab-accounts" value="accounts" className="data-[state=active]:bg-[#fbdd68]/10 data-[state=active]:text-[#fbdd68] text-[#8892b0]">
            <Building2 className="w-4 h-4 mr-1.5" /> Accounts
          </TabsTrigger>
          <TabsTrigger data-testid="settings-tab-transfers" value="transfers" className="data-[state=active]:bg-[#fbdd68]/10 data-[state=active]:text-[#fbdd68] text-[#8892b0]">
            <GitBranch className="w-4 h-4 mr-1.5" /> Transfers
          </TabsTrigger>
          <TabsTrigger data-testid="settings-tab-audit" value="audit" className="data-[state=active]:bg-[#fbdd68]/10 data-[state=active]:text-[#fbdd68] text-[#8892b0]">
            <ScrollText className="w-4 h-4 mr-1.5" /> Audit Log
          </TabsTrigger>
        </TabsList>

        {/* Accounts */}
        <TabsContent value="accounts">
          <Card className="glass-panel rounded-2xl border-0">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-white text-lg">Accounts ({totalAccounts})</CardTitle>
                <form onSubmit={handleSearch} className="flex gap-2">
                  <Input data-testid="admin-accounts-search" placeholder="Search accounts..." value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)} className="bg-[#000e2a]/80 text-white placeholder-[#8892b0] border-[rgba(251,221,104,0.2)] w-56" />
                  <Button type="submit" size="sm" className="bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#ffe685]"><Search className="w-4 h-4" /></Button>
                </form>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[rgba(251,221,104,0.15)] hover:bg-transparent">
                      <TableHead className="text-[#8892b0] text-xs uppercase">Owner</TableHead>
                      <TableHead className="text-[#8892b0] text-xs uppercase">License</TableHead>
                      <TableHead className="text-[#8892b0] text-xs uppercase">Status</TableHead>
                      <TableHead className="text-[#8892b0] text-xs uppercase">Progs</TableHead>
                      <TableHead className="text-[#8892b0] text-xs uppercase">Hosts</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounts.map((a) => (
                      <TableRow key={a.account_id} data-testid={`admin-account-row-${a.account_id}`} className="border-[rgba(251,221,104,0.08)] hover:bg-white/[0.02]">
                        <TableCell className="text-white text-sm">{a.owner_email}</TableCell>
                        <TableCell className="font-mono text-[#8892b0] text-xs whitespace-nowrap">
                          <span data-testid={`license-key-${a.account_id}`}>{a.license_key_masked || 'Not set'}</span>
                          <button data-testid={`edit-license-key-${a.account_id}`} onClick={() => openKeyEditor(a)}
                            className="ml-2 inline-flex items-center text-[#fbdd68]/70 hover:text-[#fbdd68] align-middle" title="Edit license key">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </TableCell>
                        <TableCell>
                          <Badge className={a.subscription_status === 'active' ? 'bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30' : 'bg-[#ef4444]/10 text-[#ef4444] border-[#ef4444]/30'}>
                            {a.subscription_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-white">{a.program_count}</TableCell>
                        <TableCell className="text-white">{a.host_count}</TableCell>
                      </TableRow>
                    ))}
                    {accounts.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-[#8892b0] py-8">No accounts found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transfer requests (branch-off approvals) */}
        <TabsContent value="transfers">
          <TransferRequests />
        </TabsContent>

        {/* Audit Log */}
        <TabsContent value="audit">
          <Card className="glass-panel rounded-2xl border-0">
            <CardHeader><CardTitle className="text-white text-lg">Recent Activity</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {auditLog.map((entry) => (
                  <div key={entry._id} className="flex items-start gap-3 p-3 rounded-lg bg-[rgba(20,27,80,0.4)] border border-[rgba(251,221,104,0.06)]">
                    <ScrollText className="w-4 h-4 text-[#fbdd68] mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium">{entry.action.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-[#8892b0] truncate">{entry.actor_email} — {entry.details}</p>
                      <p className="text-[10px] text-[#8892b0]/50 mt-0.5">{new Date(entry.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
                {auditLog.length === 0 && <p className="text-center text-[#8892b0] py-8">No activity yet</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editAcct} onOpenChange={(open) => { if (!open) setEditAcct(null); }}>
        <DialogContent data-testid="license-key-dialog" className="bg-[#0a1240] border border-[rgba(251,221,104,0.2)] text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#fbdd68]">License key</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#8892b0]">{editAcct?.owner_email}</p>
          <Input data-testid="license-key-input" placeholder="BHE-XXXX-XXXX-XXXX-XXXX" value={keyInput}
            onChange={(e) => { setKeyInput(e.target.value); setKeyError(''); }}
            className="bg-[#000e2a]/80 text-white placeholder-[#8892b0] border-[rgba(251,221,104,0.2)] font-mono" />
          {keyError && <p data-testid="license-key-error" className="text-sm text-[#ef4444]">{keyError}</p>}
          <DialogFooter>
            <Button data-testid="license-key-save" onClick={saveLicenseKey} disabled={saving || !keyInput.trim()}
              className="bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#ffe685]">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
